<template>
  <div>
    <v-alert v-if="!filters.realEstateId" icon="mdi-information-outline" class="mw-500" color="blue" text>
      {{ $t('movement.messages.selectAClientToAccessMovements') }}
    </v-alert>

    <AdminMovementListFilters />

    <div v-if="filters.realEstateId && customer" class="mb-8">
      <p class="mb-1 subtitle-2 grey--text">
        {{ $t('movement.label.customerBalance') }}
      </p>
      <p class="text-h4" v-if="!moneyLoading">
        <span :class="moneyColor(customer.available_balance)">
          {{ availableBalance(customer.available_balance) }}
        </span>
      </p>
      <v-skeleton-loader v-else type="heading" class="mt-3 mb-10" />
    </div>

    <v-card outlined>
      <v-data-table
        @dblclick:row="showMovementRow"
        mobile-breakpoint="20"
        :items-per-page="itemsPerPage"
        :no-data-text="$t('appointment.table.noData')"
        :loading-text="$t('appointment.table.loading')"
        :headers="headers"
        :loading="isLoading"
        :items="movements"
        class="clickable"
        hide-default-footer
        disable-sort
      >
        <template v-slot:[`item.real_estate`]="{ item }">
          {{ customerName(item) }}
        </template>
        <template v-slot:[`item.created_at`]="{ item }">
          {{ createdAt(item) }}
        </template>
        <template v-slot:[`item.description`]="{ item }">
          <span v-html="description(item)" />
        </template>
        <template v-slot:[`item.total`]="{ item }">
          <span v-if="item.total" :class="moneyColor(item.total)">
            {{ formatMoney(item.total) }}
          </span>
          <span v-else>---</span>
        </template>
        <template v-slot:[`item.category`]="{ item }">
          <v-chip :color="category(item).color" small>
            <v-icon class="mr-1" x-small>{{ category(item).icon }}</v-icon>
            {{ category(item).label }}
          </v-chip>
        </template>
        <template v-slot:[`item.reason`]="{ item }">
          {{ reason(item) }}
        </template>
      </v-data-table>
    </v-card>

    <h-pagination :fetch="fetchMovementsAsAdmin" />

    <RightDrawer :more-info="false">
      <MovementServiceSummary :loading="loading" />
    </RightDrawer>
  </div>
</template>
<script>
import Loading from '@/components/app/Loading'
import RightDrawer from '@/components/app/RightDrawer'
import RechargeCreate from '@/components/recharge/RechargeCreate'
import MovementServiceSummary from '@/components/movement/MovementServiceSummary'
import AdminMovementListFilters from './components/AdminMovementListFilters'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { adminMovementHeaders } from '../listHeaders/movementHeaders'
import { catalogDate, catalogMonetary } from '@/util/catalog'
import { handleParams } from '@/util/routes'
import router from '@/router'
export default {
  name: 'AdminMovementList',

  components: {
    Loading,
    RightDrawer,
    RechargeCreate,
    AdminMovementListFilters,
    MovementServiceSummary,
  },

  watch: {
    filters(value) {
      if (value.realEstateId) {
        this.moneyLoading = true
        this.fetchCustomer(value.realEstateId)
            .finally(() => (this.moneyLoading = false))
      }
    }
  },

  computed: {
    ...mapGetters([
      'user',
      'customer',
      'movement',
      'isLoading',
      'movements',
      'itemsPerPage',
      'isGeneratingReport',
      'filters'
    ]),
  },

  methods: {
    ...mapActions([
      'fetchMovementAsAdmin',
      'fetchMovementsAsAdmin',
      'showErrorMessage',
      'fetchCustomer',
      'fetchAdminMovementReport',
      'fetchAdminMovementReportGroupedByBranch',
    ]),
    ...mapMutations(['setIsDrawerOpen', 'setIsLoading', 'setMovement']),

    formatDate: catalogDate,
    formatMoney: catalogMonetary,

    showMovement(id) {
      this.loading = true
      this.setMovement(null)
      this.setIsDrawerOpen(true)
      this.fetchMovementAsAdmin(id).finally(() => this.loading = false)

      handleParams('admin-movements', id)
    },

    showMovementRow(event, { item }) {
      this.showMovement(item?.id)
    },

    openCreateRechargeWithParams() {
      const params = this.$route.query

      this.$nextTick(() => {
        if (params.createRecharge === 'true')
          this.$refs.rechargeCreate.$refs.createModal.dialog = true
      })
    },

    moneyColor(amount) {
      if (!amount) return 'grey--text text--lighten-1'
      return amount > 0 ? 'green--text text--lighten-1' : 'red--text text--lighten-1'
    },

    availableBalance(amount) {
      return amount ? this.formatMoney(amount) : '---'
    },

    customerName(item) {
      return item.real_estate.name ?? '---'
    },

    createdAt(item) {
      return item.created_at ? this.formatDate(item.created_at) : '---'
    },

    category(item) {
      return item.category === 'RECHARGE' ? this.rechargeObj : this.expenditureObj
    },

    reason(item) {
      return item.reason ? this.$t(`movement.reason.${item.reason}`) : '---'
    },

    description(item) {
      const description = item.description
      const rechargeComment = item.recharge_comment

      if (description && rechargeComment) return `
          <h6 class="mt-1 text-uppercase grey--text">${this.$t('movement.label.comment')}</h6>
          <p class="mb-2">${description}</p>
          <h6 class="text-uppercase grey--text">${this.$t('movement.label.rechargeNote')}</h6>
          <p class="mb-1">${rechargeComment}</p>
        `

        if (description) return description
    },
  },

  data() {
    return {
      headers: adminMovementHeaders,
      moneyLoading: false,
      loading: false,

      rechargeObj: {
        icon: 'mdi-arrow-down',
        color: 'green lighten-4',
        label: this.$t('movement.category.RECHARGE'),
      },

      expenditureObj: {
        icon: 'mdi-arrow-up',
        color: 'red lighten-4',
        label: this.$t('movement.category.EXPENDITURE'),
      },
    }
  },

  mounted() {
    const movementId = router.currentRoute.query.id
    movementId && this.showMovement(movementId)

    this.openCreateRechargeWithParams()
  },
}
</script>
